import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFormActions } from '../../../utils/form-helpers';
import rewardGiverInfoFormConfig from '../../../payment/reward-giver-info-form-config';
import ModalWithHeader from '../../../components/modal-with-header/ModalWithHeader';
import { listOfWordsToCommaSeparatedText, pluralize } from '../../../utils';
import Input2 from '../../../components/input/input-2/Input2';
import { SectionTitle } from '../../../components/typography/Heading';
import styles from './reward-info-form.module.less';
import RoundedButtonPrimary from '../../../components/button/rounded-button-primary/RoundedButtonPrimary';
import { determineCartContent, LOTTERY, NORMAL_REWARDS, PRODUCT_REWARDS } from '../cart/cart-model';
import PhoneNumberInput from '../../../components/input/PhoneNumberInput';
import _ from 'lodash';

const { setFormField, setFormFields, validateForm, validateField } = getFormActions(rewardGiverInfoFormConfig);

const getTitleBasedOnRewards = (rewardTypes) => {
    if (rewardTypes?.length > 1) {
        return 'Oppgi adresse';
    } else {
        const type = rewardTypes?.[0]?.type;
        const count = rewardTypes?.[0]?.count;
        switch (type) {
            case LOTTERY:
                return `Send ${pluralize(count, 'premien', 'premiene')} til`;
            case PRODUCT_REWARDS:
                return `Send ${pluralize(count, 'produktet', 'produktene')} til`;
            case NORMAL_REWARDS:
                return `Send ${pluralize(count, 'belønningen', 'belønningene')} til`;
            default:
                return 'Oppgi adresse';
        }
    }
};

const RewardInfoForm = ({ closeModal, modalIsOpen, rewards }) => {
    const dispatch = useDispatch();
    const baseURL = useSelector((state) => state.baseURL);
    const rewardGiverInfoForm = useSelector((state) => state.rewardGiverInfoForm);
    const needsAddress = rewards?.some((r) => r.needsAddress);
    const needsPhonenumber = rewards?.some((r) => r.needsPhonenumber);
    const rewardTypes = useMemo(() => determineCartContent(rewards), [rewards]);
    const title = useMemo(() => getTitleBasedOnRewards(rewardTypes), [rewardTypes]);
    const introText = listOfWordsToCommaSeparatedText([
        'navn',
        ...(needsAddress ? ['adresse'] : []),
        ...(needsPhonenumber ? ['telefonnummer'] : []),
    ]);

    useEffect(() => {
        dispatch(setFormField('needsPhonenumber', needsPhonenumber));
        dispatch(setFormField('needsAddress', needsAddress));
    }, [needsAddress, needsPhonenumber, dispatch, modalIsOpen]);

    const productAndLotteryText = useMemo(() => {
        const lotteryRewardType = rewardTypes?.find((r) => r.type === LOTTERY);
        const productRewardType = rewardTypes?.find((r) => r.type === PRODUCT_REWARDS);
        return lotteryRewardType || productRewardType
            ? `Oppgi ${introText} slik at Spleis kan sende deg ${
                  lotteryRewardType ? `${pluralize(lotteryRewardType.count, 'premien', 'premiene')} om du vinner.` : ''
              } ${
                  productRewardType
                      ? lotteryRewardType
                          ? ` ${pluralize(
                                productRewardType.count,
                                'Produktet',
                                'Produktene'
                            )} sendes også til samme adresse.`
                          : `${pluralize(productRewardType.count, 'produktet', 'produktene')}.`
                      : ''
              }`
            : null;
    }, [rewardTypes, introText]);
    const normalRewardsText = useMemo(() => {
        const normalRewardType = rewardTypes?.find((r) => r.type === NORMAL_REWARDS);
        return normalRewardType
            ? `Oppgi ${introText} slik at innsamler kan sende ${pluralize(
                  normalRewardType.count,
                  'belønningen',
                  'belønningene'
              )} til deg.`
            : null;
    }, [rewardTypes, introText]);

    const { errors } = rewardGiverInfoForm;

    const fetchPostalName = useCallback(
        async (e) => {
            const postalCode = e.target.value.trim();

            if (postalCode.length !== 4) {
                return dispatch(setFormFields({ postalCode, postalName: '' }));
            }

            const url = `${baseURL}/api/public/location/postalCode/${postalCode}/postalName`;
            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
            });
            const postalName = await response.text();
            return dispatch(setFormFields({ postalCode, postalName }, true));
        },
        [baseURL, dispatch]
    );

    return (
        <ModalWithHeader
            title={title}
            contentLabel={title}
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            shouldCloseOnOverlayClick={false}
            lightTheme
        >
            <form
                className={styles.form}
                onSubmit={(e) => {
                    e.preventDefault();
                    const formIsValid = _.isEmpty(dispatch(validateForm()));

                    if (formIsValid) {
                        dispatch(setFormField('hasEnteredAddress', true));
                    }
                }}
            >
                {productAndLotteryText && (
                    <SectionTitle tag={'h1'} className={styles.forminfo}>
                        {productAndLotteryText}{' '}
                        {rewardTypes.find((r) => r.type === NORMAL_REWARDS)
                            ? `Innsamler er ansvarlig for ${pluralize(
                                  rewardTypes.find((r) => r.type === NORMAL_REWARDS)?.count,
                                  'belønningen',
                                  'belønningene'
                              )}.`
                            : ''}
                    </SectionTitle>
                )}
                {normalRewardsText && !productAndLotteryText && (
                    <SectionTitle tag={'h1'} className={styles.forminfo}>
                        {normalRewardsText}
                    </SectionTitle>
                )}

                <Input2
                    title="Fullt navn"
                    errors={errors}
                    fieldName="name"
                    autocomplete={'name'}
                    type="text"
                    onChange={(e) => dispatch(setFormField('name', e.target.value?.trim() || ''))}
                    onBlur={(e) => dispatch(setFormField('name', e.target.value.trim(), true))}
                    wrapperClass={styles.inputWrapper}
                />
                {needsPhonenumber && (
                    <PhoneNumberInput
                        errors={errors}
                        title="Telefonnummer"
                        fieldName="phoneNumber"
                        type="text"
                        onChange={(value) => dispatch(setFormField('phoneNumber', value.value))}
                        onBlur={() => dispatch(validateField('phoneNumber'))}
                        wrapperClass={styles.inputWrapper}
                    />
                )}
                {needsAddress && (
                    <>
                        <Input2
                            errors={errors}
                            title="Adresse"
                            fieldName="address"
                            type="text"
                            onChange={(e) => dispatch(setFormField('address', e.target.value.trim()))}
                            onBlur={(e) => dispatch(setFormField('address', e.target.value.trim(), true))}
                            wrapperClass={styles.inputWrapper}
                        />

                        <div>
                            <div className={styles.postalWrapper}>
                                <Input2
                                    fieldName="postalName"
                                    type="tel"
                                    title={'Postnummer/-sted'}
                                    maxLength="4"
                                    shouldShowError={false}
                                    onChange={fetchPostalName}
                                    onBlur={(e) => dispatch(setFormField('postalCode', e.target.value.trim(), true))}
                                    wrapperClass={styles.inputWrapper}
                                    errors={{ postalName: errors?.postalCode || errors?.postalName }}
                                >
                                    <span className={styles.postalPlace}>{rewardGiverInfoForm.postalName}</span>
                                </Input2>
                            </div>
                        </div>
                    </>
                )}

                <RoundedButtonPrimary label="Fortsett">Fortsett</RoundedButtonPrimary>
            </form>
        </ModalWithHeader>
    );
};

export default RewardInfoForm;
